import { AdminLayout } from "@/components/admin/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Smile, Heart, HelpCircle, RotateCcw, Gift, Palette } from "lucide-react";

const COLORS = ["#f43f5e", "#3b82f6", "#a78bfa"];

export default function PostPurchaseAnalytics() {
  const { data, isLoading } = useQuery({
    queryKey: ["post-purchase-analytics"],
    queryFn: async () => {
      const { data: reactions, error } = await supabase
        .from("post_purchase_reactions")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return reactions || [];
    },
  });

  const total = data?.length || 0;
  const wow = data?.filter((r) => r.reaction_type === "wow").length || 0;
  const normal = data?.filter((r) => r.reaction_type === "normal").length || 0;
  const notGiven = data?.filter((r) => r.reaction_type === "not_given").length || 0;

  const repeats = data?.filter((r) => r.clicked_repeat).length || 0;
  const giftMode = data?.filter((r) => r.clicked_gift_mode).length || 0;
  const otherStyle = data?.filter((r) => r.clicked_other_style).length || 0;

  const pieData = [
    { name: "😍 Вау!", value: wow },
    { name: "🙂 Нормально", value: normal },
    { name: "🤔 Не дарил", value: notGiven },
  ].filter((d) => d.value > 0);

  const conversionData = [
    { name: "Повторный хит", value: repeats, icon: RotateCcw },
    { name: "Gift Mode", value: giftMode, icon: Gift },
    { name: "Другой стиль", value: otherStyle, icon: Palette },
  ];

  const pct = (v: number) => (total ? ((v / total) * 100).toFixed(1) : "0");

  return (
    <AdminLayout title="Post-Purchase реакции">
      <div className="space-y-6">
        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <SummaryCard label="Всего ответов" value={total} />
          <SummaryCard label="😍 Вау" value={wow} sub={`${pct(wow)}%`} color="text-rose-500" />
          <SummaryCard label="🙂 Нормально" value={normal} sub={`${pct(normal)}%`} color="text-blue-500" />
          <SummaryCard label="🤔 Не дарил" value={notGiven} sub={`${pct(notGiven)}%`} color="text-violet-400" />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Pie chart */}
          <Card>
            <CardHeader><CardTitle className="text-base">Распределение реакций</CardTitle></CardHeader>
            <CardContent className="h-64">
              {total === 0 ? (
                <p className="text-muted-foreground text-sm pt-8 text-center">Нет данных</p>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                      {pieData.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Conversion bar chart */}
          <Card>
            <CardHeader><CardTitle className="text-base">Конверсия в действия</CardTitle></CardHeader>
            <CardContent className="h-64">
              {total === 0 ? (
                <p className="text-muted-foreground text-sm pt-8 text-center">Нет данных</p>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={conversionData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(v: number) => [`${v} (${pct(v)}%)`, "Кол-во"]} />
                    <Bar dataKey="value" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent reactions table */}
        <Card>
          <CardHeader><CardTitle className="text-base">Последние реакции</CardTitle></CardHeader>
          <CardContent>
            {isLoading ? (
              <p className="text-muted-foreground text-sm">Загрузка...</p>
            ) : !data?.length ? (
              <p className="text-muted-foreground text-sm">Пока нет реакций</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="py-2 pr-4">Дата</th>
                      <th className="py-2 pr-4">Реакция</th>
                      <th className="py-2 pr-4">Повтор</th>
                      <th className="py-2 pr-4">Gift</th>
                      <th className="py-2">Стиль</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.slice(0, 50).map((r) => (
                      <tr key={r.id} className="border-b border-border/50">
                        <td className="py-2 pr-4 whitespace-nowrap">{new Date(r.created_at).toLocaleDateString("ru-RU")}</td>
                        <td className="py-2 pr-4">
                          {r.reaction_type === "wow" ? "😍 Вау" : r.reaction_type === "normal" ? "🙂 Норм" : "🤔 Не дарил"}
                        </td>
                        <td className="py-2 pr-4">{r.clicked_repeat ? "✅" : "—"}</td>
                        <td className="py-2 pr-4">{r.clicked_gift_mode ? "✅" : "—"}</td>
                        <td className="py-2">{r.clicked_other_style ? "✅" : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}

function SummaryCard({ label, value, sub, color }: { label: string; value: number; sub?: string; color?: string }) {
  return (
    <Card>
      <CardContent className="pt-4 pb-3 px-4">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className={`text-2xl font-bold ${color || ""}`}>{value}</p>
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      </CardContent>
    </Card>
  );
}
